import { Box } from "@mui/material"
import { AddOutlined } from "@mui-symbols-material/w300"
import type { Row } from "@tanstack/react-table"
import type { FC } from "react"

import { CostBreakdownPurchaseOrdersTable } from "./CostBreakdownPurchaseOrdersTable"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { Button } from "@/components/common/Button"
import { PermissionActionType } from "@/constants/permissions"
import type { GoodsReceipt } from "@/graphql/codegen/graphql"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { CostBreakdownSummary } from "@/screens/Procurement/CostBreakdown/components/CostBreakdownSummary"
import { SectionHeading } from "@/screens/Procurement/CostBreakdown/components/SectionHeading"

interface CostBreakdownExpandedContentProps {
  row: Row<GoodsReceipt>
  onEdit: (goodsReceipt: GoodsReceipt, purchaseOrderLineId?: string) => void
}

export const CostBreakdownExpandedContent: FC<CostBreakdownExpandedContentProps> = ({ row, onEdit }) => {
  const goodsReceipt = row.original
  const hasCostBreakdown = Boolean(goodsReceipt.costBreakdown)
  const { hasPermissions } = usePermissionManager()
  const canMaintain = hasPermissions([PermissionActionType.COST_BREAKDOWN_MAINTENANCE])
  const canAddCostBreakdown = canMaintain && !goodsReceipt.isCostManagedInAs400

  const handleAddCostBreakdown = () => {
    onEdit(goodsReceipt)
  }

  return (
    <Box className='p-6'>
      <SectionHeading>Cost Breakdown</SectionHeading>

      {hasCostBreakdown ? (
        <CostBreakdownSummary costBreakdown={goodsReceipt.costBreakdown!} />
      ) : (
        canAddCostBreakdown && (
          <InformationMissingAlert
            title='Add Cost Breakdown'
            action={
              <Button
                color='inherit'
                size='small'
                appearance='outlined'
                className='text-primary'
                startIcon={<AddOutlined />}
                onClick={handleAddCostBreakdown}
                data-testid='add-cost-breakdown-button'
              >
                Cost Breakdown
              </Button>
            }
            className='mb-4'
          />
        )
      )}
      <CostBreakdownPurchaseOrdersTable goodsReceipt={goodsReceipt} canMaintain={canMaintain} onEdit={onEdit} />
    </Box>
  )
}
